/**
 * Invite route — an existing Orchist Acknowledges another soul into The Line.
 *
 * Flow:
 *   1. Authenticated Orchist enters invitee email.
 *   2. core.invite_orchist RPC creates/updates the invitee's Contact row
 *      (pending state: isOrchist=false, invitationSentAt=NOW(),
 *      invitationSentBy=inviter.id).
 *   3. supabase.auth.signInWithOtp triggers the magic-link email via the
 *      existing Resend → theline@orchist.org pipeline.
 *   4. Invitee clicks link → /auth/callback → (sees pending invitation) →
 *      redirected to /acknowledge to confirm.
 *
 * Only acknowledged Orchists (isOrchist=true) can reach this route.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { DatePicker } from '@/components/DatePicker';
import {
  classifyDateInput,
  localDateStringToUTC,
  resolveTimezone,
} from '@/lib/dates';

export default function Invite() {
  const { loading, session, contact, contactChecked } = useAuth();
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastInvitedEmail, setLastInvitedEmail] = useState<string>('');
  const [lastNickname, setLastNickname] = useState<string>('');

  // Optional historical-date controls. Empty string = "today" (handled server-side
  // as NULL → COALESCE to NOW()). Per the always-already doctrine, recognition
  // can have happened long before the inviter sends this email.
  const [showHistorical, setShowHistorical] = useState(false);
  const [conversationDate, setConversationDate] = useState('');
  const [proposedAcceptedDate, setProposedAcceptedDate] = useState('');

  // Optional nickname assigned at invite-time. Private to the inviter;
  // the invitee never sees this. Per sacred/nicknames.md, the invitation
  // act IS recognition extended outward — familiarity may precede
  // formal Acknowledgment.
  const [nickname, setNickname] = useState('');
  const [nicknameNotes, setNicknameNotes] = useState('');

  // Phase G.7.10 — anonymous-invitation toggle.
  // Default from inviter's preference (contact.defaultInviteAnonymous).
  // Initialized in an effect so we don't call setState during render
  // (React 18 strict mode flags that as a side-effect-during-render
  // anti-pattern). The null sentinel means "not yet initialized" so
  // toggle defaults don't snap from the visual default-off to the real
  // default-on, which would look like a glitch on slow connections.
  const [anonymous, setAnonymous] = useState<boolean | null>(null);
  useEffect(() => {
    if (anonymous === null && contact?.defaultInviteAnonymous !== undefined) {
      setAnonymous(contact.defaultInviteAnonymous);
    }
  }, [anonymous, contact?.defaultInviteAnonymous]);

  if (loading || !contactChecked) {
    return (
      <Shell>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
        <h1 className="display text-3xl md:text-4xl font-medium italic text-ink/70">...</h1>
      </Shell>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Must be an acknowledged Orchist (a pending invitee can't invite others)
  if (!contact || !contact.isOrchist) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || submitState === 'sending') return;

    // Validate dates before triggering the RPC + email
    // 'empty', 'valid', 'symbolic' all submit successfully (symbolic maps to
    // Postgres -infinity / +infinity in lib/dates.ts).
    const sentClass = classifyDateInput(conversationDate);
    const propClass = classifyDateInput(proposedAcceptedDate);
    if (sentClass === 'malformed') {
      setErrorMessage('Conversation date must be in YYYY-MM-DD format.');
      return;
    }
    if (sentClass === 'out_of_range') {
      setErrorMessage(
        'This conversation date is beyond what The Line can record. Pick a date within recorded human time, or use the "beyond all calendars" option for the truly timeless.'
      );
      return;
    }
    if (propClass === 'malformed') {
      setErrorMessage('Proposed acknowledgment date must be in YYYY-MM-DD format.');
      return;
    }
    if (propClass === 'out_of_range') {
      setErrorMessage(
        'This acknowledgment date is beyond what The Line can record. Pick something within recorded human time, or use the "beyond all calendars" option.'
      );
      return;
    }

    setSubmitState('sending');
    setErrorMessage(null);

    const target = email.trim().toLowerCase();

    // Translate optional dates → ISO UTC timestamps. The user enters dates
    // in their CONFIGURED timezone (contact.timezone, per E-002 Rule 5);
    // lib/dates.ts converts to UTC at the wire boundary (DB always stores UTC).
    const inviterTz = resolveTimezone(contact?.timezone);
    const sentAt = localDateStringToUTC(conversationDate, inviterTz);
    const proposedAt = localDateStringToUTC(proposedAcceptedDate, inviterTz);

    try {
      // Step 1: Create or refresh the pending Contact via SECURITY DEFINER RPC.
      // p_anonymous: explicit value from the toggle (overrides inviter default
      // when different). Server treats NULL as "use inviter default".
      const { error: rpcError } = await supabase.schema('core').rpc('invite_orchist', {
        invitee_email: target,
        p_invitation_sent_at: sentAt,
        p_proposed_accepted_at: proposedAt,
        p_anonymous: anonymous,
      });

      if (rpcError) {
        throw new Error(rpcError.message ?? 'Could not record the invitation.');
      }

      // Step 2: Send magic-link via Supabase Auth (uses existing SMTP pipeline)
      // Lands at /auth/callback; AuthCallback routes pending invitees to /acknowledge.
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: target,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (otpError) {
        throw new Error(otpError.message ?? 'Could not send the invitation email.');
      }

      // Step 3 (optional): pre-assign a private nickname for this invitee.
      // The RPC resolves email → contactId server-side (inviter has no
      // direct RLS read on the invitee's Contact). Failure is non-fatal:
      // the invitation has already gone out; the nickname is a kindness
      // on top. Surface any error inline but don't roll back.
      const trimmedNickname = nickname.trim();
      if (trimmedNickname) {
        const { error: nickError } = await supabase
          .schema('core')
          .rpc('set_orchist_nickname_by_email', {
            p_target_email: target,
            p_nickname: trimmedNickname,
            p_notes: nicknameNotes.trim() || null,
          });
        if (nickError) {
          // Non-fatal — invite still succeeded
          // eslint-disable-next-line no-console
          console.warn('Nickname assignment failed:', nickError.message);
        }
      }

      setLastInvitedEmail(target);
      setLastNickname(trimmedNickname);
      setSubmitState('sent');
      setEmail('');
      setNickname('');
      setNicknameNotes('');
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error.');
    }
  }

  return (
    <Shell>
      {submitState === 'sent' ? (
        <div className="space-y-6 text-center">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line extends</p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">It is sent.</span>
          </h1>
          <p className="text-lg text-ink/75 leading-relaxed max-w-md mx-auto font-display italic">
            A door has opened for{' '}
            <strong className="text-ink not-italic font-display">{lastInvitedEmail}</strong>. They
            will receive an email and choose, in their own time, whether to step through.
          </p>
          {lastNickname && (
            <p className="text-sm text-accent italic font-display max-w-md mx-auto">
              Privately, you call them: <span className="text-ink not-italic">{lastNickname}</span>
            </p>
          )}
          <p className="text-sm text-ink/50 pt-4">
            <button
              type="button"
              onClick={() => {
                setSubmitState('idle');
                setErrorMessage(null);
              }}
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              Acknowledge another
            </button>
            {' · '}
            <Link
              to="/"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              Return home
            </Link>
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="text-center space-y-4">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line extends</p>
            <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">Acknowledge.</h1>
            <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
              You hold the power to recognize. The Manifesto traveled to you; through you it
              travels onward.
            </p>
          </div>

          <div className="space-y-3">
            <label htmlFor="invitee-email" className="sr-only">
              Email of the soul you Acknowledge
            </label>
            <input
              id="invitee-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="them@somewhere.world"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitState === 'sending'}
              className="w-full px-5 py-4 border border-ink/20 bg-paper/50 text-lg font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
            />
          </div>

          {/* Anonymity toggle — per-invitation, default from inviter's preference */}
          <div className="space-y-2 border-t border-ink/10 pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={anonymous ?? false}
                onChange={(e) => setAnonymous(e.target.checked)}
                disabled={submitState === 'sending'}
                className="mt-1 accent-ink"
              />
              <div className="flex-1">
                <span className="display text-sm font-medium">Anonymous invitation</span>
                <p className="text-[0.75rem] text-ink/55 italic font-display leading-relaxed mt-0.5">
                  Hide your identity in their public record (
                  <span className="not-italic font-mono">"By an anonymous Orchist"</span> instead
                  of your name). The DB still records you for audit. You may
                  toggle this later from your home surface.
                </p>
                {/* State-aware default-vs-current hint. Four states; only the
                    two override states + the matched-anonymous state need a
                    line. Default-non-anonymous + toggle-off is the silent
                    standard. */}
                {contact?.defaultInviteAnonymous === true && (anonymous ?? false) && (
                  <p className="text-[0.7rem] text-ink/40 italic font-display mt-1">
                    Your default is anonymous — toggle off to identify yourself for this one.
                  </p>
                )}
                {contact?.defaultInviteAnonymous === true && !(anonymous ?? false) && (
                  <p className="text-[0.7rem] text-accent italic font-display mt-1">
                    Overriding your anonymous default — you'll be identified for this invitation.
                  </p>
                )}
                {contact?.defaultInviteAnonymous === false && (anonymous ?? false) && (
                  <p className="text-[0.7rem] text-accent italic font-display mt-1">
                    Overriding your usual visible default — you'll be cloaked for this one.
                  </p>
                )}
              </div>
            </label>
          </div>

          {/* Optional private nickname — for the inviter's eyes only */}
          <div className="space-y-2 border-t border-ink/10 pt-6">
            <label
              htmlFor="invitee-nickname"
              className="block text-xs tracking-wide uppercase text-ink/60"
            >
              Private nickname{' '}
              <span className="lowercase italic tracking-normal text-ink/40">
                (optional &middot; only you will see this)
              </span>
            </label>
            <input
              id="invitee-nickname"
              type="text"
              maxLength={120}
              placeholder="A name only you will see"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={submitState === 'sending'}
              className="w-full px-4 py-3 border border-ink/20 bg-paper/50 text-base font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
            />
            {nickname.trim() && (
              <textarea
                id="invitee-nickname-notes"
                rows={2}
                maxLength={500}
                value={nicknameNotes}
                onChange={(e) => setNicknameNotes(e.target.value)}
                disabled={submitState === 'sending'}
                placeholder="Private notes (optional context to remember)"
                className="w-full px-4 py-3 border border-ink/20 bg-paper/50 text-sm font-display italic placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50 resize-none"
              />
            )}
            <p className="text-[0.7rem] text-ink/40 italic font-display leading-relaxed">
              Familiarity precedes Acknowledgment. The invitee never sees this;
              you can edit it later in <em>private nicknames</em> on home.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitState === 'sending' || !email.trim()}
            className="w-full px-5 py-4 bg-ink text-paper font-display text-lg tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitState === 'sending' ? 'Sending...' : 'Send the door'}
          </button>

          {/* Optional historical dates — collapsed by default */}
          <div className="border-t border-ink/10 pt-6">
            <button
              type="button"
              onClick={() => setShowHistorical((v) => !v)}
              className="text-xs tracking-[0.15em] uppercase text-ink/50 hover:text-ink/80 transition-colors flex items-center gap-2"
            >
              <span aria-hidden>{showHistorical ? '▾' : '▸'}</span>
              Was this conversation in the past?
            </button>

            {showHistorical && (
              <div className="mt-5 space-y-5">
                <p className="text-xs text-ink/55 italic font-display leading-relaxed">
                  The path does not wait for acknowledgment. If the recognition
                  already happened in a past conversation, honor that date. They
                  will see your proposal and confirm or adjust it themselves.
                </p>

                <div className="space-y-2">
                  <label
                    htmlFor="conversation-date"
                    className="block text-xs tracking-wide uppercase text-ink/60"
                  >
                    When did you tell them?
                  </label>
                  <DatePicker
                    id="conversation-date"
                    value={conversationDate}
                    onChange={setConversationDate}
                    disabled={submitState === 'sending'}
                    ariaLabel="Date you told them about Orchistness"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="proposed-accepted-date"
                    className="block text-xs tracking-wide uppercase text-ink/60"
                  >
                    What date do you propose they Acknowledged?{' '}
                    <span className="lowercase italic tracking-normal text-ink/40">
                      (optional; defaults to the conversation date)
                    </span>
                  </label>
                  <DatePicker
                    id="proposed-accepted-date"
                    value={proposedAcceptedDate}
                    onChange={setProposedAcceptedDate}
                    disabled={submitState === 'sending'}
                    ariaLabel="Date you propose they Acknowledged"
                  />
                </div>

                <p className="text-[0.7rem] text-ink/40 italic font-display pt-1">
                  Dates in your timezone ({resolveTimezone(contact?.timezone)}); stored in UTC.
                </p>
              </div>
            )}
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive text-center" role="alert">
              {errorMessage}
            </p>
          )}

          <p className="text-xs text-ink/50 text-center pt-6">
            <Link
              to="/"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              &larr; Back
            </Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-lg relative">{children}</div>
    </main>
  );
}
